<template>
  <div>
    <Head title="Clientes" />

    <!-- Header y Barra de herramientas -->
    <app-toolbar 
      name="Cliente" 
      namePlural="Clientes" 
      createHref="/clients/create" 
      :exportButtons="true"
    >
      <!-- Filtros de búsqueda -->
      <template v-slot:search>
        <search-filter 
          v-model="form.search" 
          class="mr-4 w-full max-w-md" 
          @reset="reset"
        >
          <label class="block text-gray-700 mb-1">Estado:</label>
          <select 
            v-model="form.trashed" 
            class="form-select w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
          >
            <option :value="null">Todos</option>
            <option value="with">Con eliminados</option>
            <option value="only">Solo eliminados</option>
          </select>
        </search-filter>
      </template>

      <!-- Botones de exportación -->
      <template v-slot:exportArea>
        <div class="flex items-center gap-2">
          <a 
            target="_blank" 
            href="/clientsReports/excel"
            class="p-2.5 bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
            title="Exportar a Excel"
          >
            <i class="fa fa-file text-white" />
          </a>
          <a 
            target="_blank" 
            href="/clientsReports/pdf"
            class="p-2.5 bg-red-700 hover:bg-red-800 rounded-lg transition-colors"
            title="Exportar a PDF"
          >
            <i class="fa fa-print text-white" />
          </a>
        </div>
      </template>
    </app-toolbar>

    <!-- Tabla de clientes -->
    <div class="bg-white rounded-lg shadow-sm overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <thead>
          <tr class="text-left bg-gray-50">
            <th class="py-4 px-6 font-semibold">Nombre</th>
            <th class="py-4 px-6 font-semibold">Alias o Cédula</th>
            <th class="py-4 px-6 font-semibold">Correo electrónico</th>
            <th class="py-4 px-6 font-semibold">Tipo de cliente</th>
            <th class="py-4 px-6 font-semibold">Empresa</th>
            <th class="py-4 px-6 font-semibold">Dirección</th>
            <th class="py-4 px-6 font-semibold">Adeudado</th>
            <th class="py-4 px-6 font-semibold" colspan="2">Teléfono</th>
          </tr>
        </thead>
        <tbody>
          <tr 
            v-for="client in clients.data" 
            :key="client.id" 
            :class="{
              'hover:bg-gray-50 focus-within:bg-gray-50': true,
              'bg-red-100': client.balance > 0
            }"
          >
            <td class="border-t">
              <Link 
                class="flex items-center px-6 py-4 hover:text-blue-600 focus:text-blue-600 transition-colors" 
                :href="`/clients/${client.id}`"
              >
                {{ client.name }}
                <icon 
                  v-if="client.deleted_at" 
                  name="trash" 
                  class="ml-2 w-4 h-4 text-gray-400"
                  title="Cliente eliminado"
                />
              </Link>
            </td>
            <td class="border-t">
              <Link 
                class="block px-6 py-4 hover:text-blue-600" 
                :href="`/clients/${client.id}`"
              >
                {{ client.identification }}
              </Link>
            </td>
            <td class="border-t">
              <Link 
                class="block px-6 py-4 hover:text-blue-600" 
                :href="`/clients/${client.id}`"
              >
                {{ client.email }}
              </Link>
            </td>
            <td class="border-t">
              <Link 
                class="block px-6 py-4 hover:text-blue-600"
                :href="`/clients/${client.id}`"
              >
                {{ client.typeClient }}
              </Link>
            </td>
            <td class="border-t">
              <Link 
                class="block px-6 py-4 hover:text-blue-600"
                :href="`/clients/${client.id}`"
              >
                {{ client.companyName }}
              </Link>
            </td>
            <td class="border-t">
              <Link 
                class="block px-6 py-4 hover:text-blue-600"
                :href="`/clients/${client.id}`"
              >
                {{ client.address }}
              </Link>
            </td>
            <td class="border-t">
              <Link 
                class="block px-6 py-4 hover:text-blue-600"
                :href="`/clients/${client.id}`"
              >
                {{ formatCurrency(client.balance) }}
              </Link>
            </td>
            <td class="border-t">
              <Link 
                class="block px-6 py-4 hover:text-blue-600"
                :href="`/clients/${client.id}`"
              >
                {{ client.phone }}
              </Link>
            </td>
            <td class="w-px border-t">
              <Link 
                class="block p-4 hover:text-blue-600"
                :href="`/clients/${client.id}`"
              >
                <icon 
                  name="cheveron-right" 
                  class="w-6 h-6 text-gray-400"
                />
              </Link>
            </td>
          </tr>

          <tr v-if="clients.data.length === 0">
            <td 
              class="px-6 py-8 text-center text-gray-500 border-t" 
              colspan="10"
            >
              No hay clientes disponibles
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <pagination 
      v-if="clients.links" 
      :links="clients.links" 
      class="mt-6"
    />
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination'
import SearchFilter from '@/Shared/SearchFilter'
import AppToolbar from '../../Shared/AppToolbar.vue'

export default {
  name: 'ClientsIndex',

  components: {
    Head,
    Icon,
    Link,
    Pagination,
    SearchFilter,
    AppToolbar,
  },

  layout: Layout,

  props: {
    filters: Object,
    clients: Object,
  },

  data() {
    return {
      form: {
        search: this.filters.search,
        trashed: this.filters.trashed,
      }
    }
  },

  watch: {
    form: {
      deep: true,
      handler: throttle(function() {
        this.$inertia.get('/clients', pickBy(this.form), { 
          preserveState: true 
        })
      }, 300),
    },
  },

  methods: {
    back() {
      window.history.back()
    },

    reset() {
      this.form = mapValues(this.form, () => null)
    },

    formatCurrency(value) {
      return new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: 'COP'
      }).format(value)
    }
  },
}
</script>
